/**
 * Dirty GENERATION for the encounter questionnaire (PROD-2700).
 *
 * A boolean `dirty` cannot tell "the edit I just saved" from "an edit made while that save was in
 * flight". The save sends a snapshot of the answers taken when it started, so clearing a boolean on
 * success also cleared any edit typed during the request: the page believed everything was saved,
 * nothing scheduled another save, and leaving the encounter dropped those edits.
 *
 * So every edit bumps `edited`; a save records the `edited` value it snapshotted; its success raises
 * `saved` to that value. The document is dirty while `edited > saved`, which stays true after a save
 * that did not carry the newest edit, and the autosave then sends the newer content.
 *
 * Pure and per-document by design: a future per-answer-set model (the ticket's Part 3, one id, dirty
 * generation and save queue per set) keeps one of these per set.
 */
export interface DirtyGeneration {
  /** Bumped by every edit. */
  readonly edited: number;
  /** The highest `edited` value a successful save is known to have carried. */
  readonly saved: number;
}

export const CLEAN_GENERATION: DirtyGeneration = { edited: 0, saved: 0 };

/** An edit happened. */
export function markEdited(state: DirtyGeneration): DirtyGeneration {
  return { edited: state.edited + 1, saved: state.saved };
}

/**
 * A save that snapshotted generation `sentGeneration` succeeded. Never moves `saved` backwards (an
 * older save settling late cannot un-save a newer one) and never past `edited`.
 */
export function markSaved(state: DirtyGeneration, sentGeneration: number): DirtyGeneration {
  const saved = Math.max(state.saved, Math.min(sentGeneration, state.edited));
  return saved === state.saved ? state : { edited: state.edited, saved };
}

/** Edits exist that no successful save has carried. */
export function isDirty(state: DirtyGeneration): boolean {
  return state.edited > state.saved;
}
